/**
 * Taking an Environment off this device. The machine itself keeps running and stays reachable
 * from the user's other devices; only what this device saved to reach it is forgotten.
 */
import {
  type ConnectDesktopBridge,
  EnvironmentId,
  type PairedConnectionSetting,
  type SshConnectionSetting,
} from "@afar-research/contracts";
import * as Effect from "effect/Effect";

import { forgetCloudWorkspace } from "./cloudWorkspaceRegistration";
import {
  connectAccountKey,
  removeConnectEnvironment,
  requestConnectEnvironmentsRefresh,
} from "./connectEnvironments";
import { forgetPairedConnection } from "./pairedConnections";
import { forgetEnvironmentPreferences } from "./savedConnections";
import { forgetSshConnection } from "./sshConnections";
import { makeSshEnvironmentGateway } from "./sshEnvironment";

/** One machine this device saved itself, however it reaches it. */
export type SavedEnvironment =
  | { readonly kind: "ssh"; readonly connection: SshConnectionSetting }
  | { readonly kind: "paired"; readonly connection: PairedConnectionSetting };

/** Forgets a saved SSH or Remote link machine: its route, its pairing, and its preferences here. */
export async function removeSavedEnvironment(saved: SavedEnvironment): Promise<void> {
  const { environmentId } = saved.connection;
  if (saved.kind === "ssh") {
    await forgetSshConnection(environmentId);
    // Forgotten first, so the catalog stops reconnecting at once. Closing the forward follows, so
    // the machine is not left running a service this device started and no longer knows about.
    const ssh = window.desktopBridge?.ssh;
    if (ssh !== undefined) {
      void makeSshEnvironmentGateway(ssh)
        .disconnect(saved.connection.target)
        .pipe(Effect.ignore, Effect.runPromise);
    }
  } else {
    await forgetPairedConnection(environmentId);
  }
  await forgetEnvironmentPreferences(environmentId);
}

/**
 * Forgets a machine AFAR Connect introduced — a Cloud Workspace or a linked computer — on this
 * device. It comes off this device's added list, so the account's next listing offers it as not
 * on this device rather than bringing it back.
 */
export async function removeConnectEnvironmentFromDevice(
  bridge: Pick<ConnectDesktopBridge, "forgetComputer" | "getStatus" | "networks">,
  environmentId: string,
): Promise<void> {
  const status = await bridge.getStatus();
  if (status.state !== "signed-in") throw new Error("The account is signed out. Sign in again.");
  await bridge.forgetComputer?.(environmentId);
  // Clear route provenance first: reconciliation otherwise registers the guest again.
  bridge.networks?.forgetNetworkMachine(environmentId);
  const id = EnvironmentId.make(environmentId);
  // Forgotten as registered too, so adding it back registers it again rather than being skipped as
  // a workspace already connected.
  forgetCloudWorkspace(environmentId);
  await forgetEnvironmentPreferences(id);
  await removeConnectEnvironment(connectAccountKey(status.activeAccountId), id);
  requestConnectEnvironmentsRefresh();
}
